import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  TextField,
  MenuItem,
  Select,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { get, post, remove } from 'api';
import { useTranslation } from 'react-i18next';

const EditMatchResultModal = ({
  open,
  onClose,
  match,
  fetchGroups,
  fetchGroupMatches,
  fetchMainTable,
  successToastRef,
  errorToastRef,
  openedFrom,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [newResultTeam1, setNewResultTeam1] = useState('');
  const [newResultTeam2, setNewResultTeam2] = useState('');
  const [winner, setWinner] = useState('');
  const [loser, setLoser] = useState('');
  const [matchInfo, setMatchInfo] = useState(null);

  const fetchMatchInfo = async (matchId) => {
    if (!match) return;
    try {
      const response = await get(`/tournament-playoffs/match-info/${matchId}`);
      const data = await response.json();
      if (response.ok) {
        setMatchInfo(data[0]);
      } else {
        console.log('Error fetching match info:', data.msg);
      }
    } catch (error) {
      console.error('Error fetching match info:', error);
    }
  };

  useEffect(() => {
    if (
      (openedFrom === 'playoffs' || openedFrom === 'losers') &&
      typeof match === 'number' &&
      open
    ) {
      fetchMatchInfo(match);
    } else if (
      openedFrom !== 'playoffs' &&
      openedFrom !== 'losers' &&
      typeof match === 'object' &&
      open
    ) {
      setMatchInfo(match);
    }
  }, [openedFrom, match, open]);

  useEffect(() => {
    const dataSource =
      (openedFrom === 'playoffs' || openedFrom === 'losers') && open
        ? matchInfo
        : match;

    if (dataSource) {
      setNewResultTeam1(dataSource.score_team1 || '');
      setNewResultTeam2(dataSource.score_team2 || '');
      setWinner(dataSource.winner_team_id || '');
      setLoser(
        dataSource.winner_team_id === dataSource.team1_id
          ? dataSource.team2_id
          : dataSource.team1_id,
      );
    }
  }, [match, matchInfo, openedFrom]);

  const handleResultChange = async () => {
    const dataSource =
      openedFrom === 'playoffs' || openedFrom === 'losers' ? matchInfo : match;
    if (!dataSource) return;

    const {
      match_id,
      match_number,
      winner_team_id: originalWinner,
      stage,
      tournament_id,
      tournament_type,
      score_team1: existingScore1,
      score_team2: existingScore2,
    } = dataSource;

    try {
      // 1. DELETE - only if both fields are empty AND user explicitly wants to delete
      if (newResultTeam1.trim() === '' && newResultTeam2.trim() === '') {
        // If there were existing scores, this means user wants to delete the result
        if (existingScore1 || existingScore2) {
          const response = await remove(
            `/tournament-matches/delete-result/${match_id}`,
          );
          const result = await response.json();
          if (response.ok) {
            successToastRef.current.showSuccess(
              result.msg || 'Rezultatas ištrintas',
            );
            openedFrom === 'playoffs' || openedFrom === 'losers'
              ? fetchMainTable()
              : (fetchGroups(), fetchGroupMatches());
          } else {
            errorToastRef.current.showError(
              result.msg || 'Klaida trinant rezultatą',
            );
          }
          return;
        } else {
          // If there were no existing scores and both fields are empty, do nothing
          return;
        }
      }

      // 2. INSERT - Simple Quick Result
      const isSimpleInsert =
        (newResultTeam1 === '1:0' && newResultTeam2 === '0:1') ||
        (newResultTeam1 === '0:1' && newResultTeam2 === '1:0') ||
        (newResultTeam1 === 'B/Ž' && newResultTeam2 === 'B/Ž');

      if (isSimpleInsert) {
        const insertBody = {
          match_id,
          tournament_id,
          tournament_type,
          match_number,
          stage,
          ch_set1:
            newResultTeam1 === 'B/Ž' ? 'B/Ž' : newResultTeam1 === '1:0' ? 1 : 0,
          op_set1:
            newResultTeam2 === 'B/Ž' ? 'B/Ž' : newResultTeam2 === '1:0' ? 1 : 0,
          ch_set2: null,
          ch_set3: null,
          op_set2: null,
          op_set3: null,
          ch_tb1: null,
          ch_tb2: null,
          ch_tb3: null,
          op_tb1: null,
          op_tb2: null,
          op_tb3: null,
          score_team1: newResultTeam1,
          score_team2: newResultTeam2,
          winner_team_id: newResultTeam1 === 'B/Ž' ? 0 : winner,
          loser_team_id: newResultTeam2 === 'B/Ž' ? 0 : loser,
        };

        const response = await post(
          `/tournament-matches/insert-result`,
          insertBody,
        );
        const result = await response.json();
        if (response.ok) {
          successToastRef.current.showSuccess(
            result.msg || 'Rezultatas įvestas',
          );
          openedFrom === 'playoffs' || openedFrom === 'losers'
            ? fetchMainTable()
            : (fetchGroups(), fetchGroupMatches());
        } else {
          errorToastRef.current.showError(
            result.msg || 'Klaida įvedant rezultatą',
          );
        }
        return;
      }

      // 3. FULL RESULT PARSING (used in both insert and update)
      const updatedFields = {};
      const newSetsTeam1 = newResultTeam1.split(' ');
      newSetsTeam1.forEach((set, index) => {
        const regex = /(\d+)?(?:\((\d+)\))?:\s*(\d+)?(?:\((\d+)\))?/;
        const match = regex.exec(set);
        if (match) {
          const [, team1Score, team1TB, team2Score, team2TB] = match.map(
            (val) => (val !== undefined ? parseInt(val, 10) : null),
          );
          updatedFields[`ch_set${index + 1}`] = team1Score;
          updatedFields[`op_set${index + 1}`] = team2Score;
          updatedFields[`ch_tb${index + 1}`] = team1TB;
          updatedFields[`op_tb${index + 1}`] = team2TB;
        } else {
          updatedFields[`ch_set${index + 1}`] = null;
          updatedFields[`op_set${index + 1}`] = null;
          updatedFields[`ch_tb${index + 1}`] = null;
          updatedFields[`op_tb${index + 1}`] = null;
        }
      });

      const fullResultPayload = {
        match_id,
        tournament_id,
        tournament_type,
        match_number,
        stage,
        score_team1: newResultTeam1,
        score_team2: newResultTeam2,
        winner_team_id: winner,
        loser_team_id: loser,
        ...updatedFields,
      };

      if (winner !== originalWinner && stage !== 'group') {
        const currentMatchNumber = parseInt(
          match_number.replace('P', '').replace('_rez', ''),
          10,
        );
        const nextMatchNumber = currentMatchNumber + 1;
        fullResultPayload.next_match_column = `P${nextMatchNumber}_rez`;
        fullResultPayload.next_group_column = `P${nextMatchNumber}_group`;
      }

      // 4. INSERT or UPDATE based on existing data
      // If there are existing scores in the database, we should always UPDATE
      // If there are no existing scores, we should INSERT
      // Also check if there's a winner_team_id to determine if a result exists
      const hasExistingResult =
        existingScore1 || existingScore2 || dataSource.winner_team_id;
      const shouldInsert = !hasExistingResult;

      const response = await post(
        shouldInsert
          ? `/tournament-matches/insert-result`
          : `/tournament-matches/update-result`,
        fullResultPayload,
      );
      const result = await response.json();

      if (response.ok) {
        successToastRef.current.showSuccess(
          result.msg ||
            (shouldInsert ? 'Rezultatas įvestas' : 'Rezultatas atnaujintas'),
        );
        openedFrom === 'playoffs' || openedFrom === 'losers'
          ? fetchMainTable()
          : (fetchGroups(), fetchGroupMatches());
      } else {
        errorToastRef.current.showError(result.msg || 'Klaida rezultato įraše');
      }
    } catch (error) {
      console.error('Error handling result:', error);
      errorToastRef.current.showError('Klaida apdorojant rezultatą');
    }
  };

  const handleSave = async () => {
    setLoser(winner === match.team1_id ? match.team2_id : match.team1_id);
    await handleResultChange();
    onClose();
  };

  const dataSource =
    openedFrom === 'playoffs' || openedFrom === 'losers' ? matchInfo : match;

  if (!dataSource) {
    return null; // Render nothing if no data source is available
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '500px' },
          bgcolor: 'background.paper',
          border: `2px solid ${theme.palette.grey[300]}`,
          boxShadow: 24,
          p: { xs: 2, sm: 4 },
          borderRadius: '8px',
        }}
      >
        <Typography variant="h6" mb={2}>
          Redaguoti Rezultatą
        </Typography>

        {/* Team 1 Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {dataSource.team1_player1_name} & {dataSource.team1_player2_name}
          </Typography>
          <TextField
            label="1 Komandos Rezultatas"
            fullWidth
            value={newResultTeam1}
            onChange={(e) => setNewResultTeam1(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Team 2 Details */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {dataSource.team2_player1_name} & {dataSource.team2_player2_name}
          </Typography>
          <TextField
            label="2 Komandos Rezultatas"
            fullWidth
            value={newResultTeam2}
            onChange={(e) => setNewResultTeam2(e.target.value)}
            sx={{ mt: 1 }}
          />
        </Box>

        {/* Winner Selection */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Pasirinkite nugalėtoją
          </Typography>
          <Select
            fullWidth
            value={winner}
            onChange={(e) => {
              setWinner(e.target.value);
              setLoser(
                e.target.value === dataSource.team1_id
                  ? dataSource.team2_id
                  : dataSource.team1_id,
              );
            }}
            sx={{
              fontSize: { xs: '14px', sm: '16px' },
              '& .MuiSelect-select': {
                padding: '10px 14px',
              },
            }}
          >
            <MenuItem
              value={dataSource.team1_id}
              sx={{
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              {dataSource.team1_player1_name} & {dataSource.team1_player2_name}
            </MenuItem>
            <MenuItem
              value={dataSource.team2_id}
              sx={{
                fontSize: { xs: '14px', sm: '16px' },
              }}
            >
              {dataSource.team2_player1_name} & {dataSource.team2_player2_name}
            </MenuItem>
          </Select>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t('save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditMatchResultModal;
